// Дерево бокового меню (README §1.2, UI-кит §10.4 п.1).
//
// Три уровня отступов, одинаковые во всех разделах:
//   0 — заголовок раздела (СЕССИИ, СХЕМЫ…): стрелка на 16 px, 15/600 uppercase;
//   1 — подраздел или пункт без подразделов: стрелка на 26 px (6 + 20);
//   2 — пункт внутри подраздела: 46 px (6 + 40).
// Свёрнутость хранится в appStore.sideFold[screen + ':' + id] на время сессии.
// На телефоне клик по пункту закрывает шторку, клик по подразделу — только
// сворачивает его.
//
// Раздел описывает меню данными (SideSection[]), а не разметкой, поэтому
// отступы, счётчики и сокращение при сжатии везде одинаковые.

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { isMobileLayout, useAppStore } from '../store/appStore'
import { Chevron } from './Icon'
import './ui.css'

/** Цвет подписи справа (статусы — цветным текстом, §10.4 п.4). */
export type SideTone = 'dim' | 'soft' | 'ok' | 'warn' | 'rec' | 'link'

export interface SideItem {
  /** уникален в пределах раздела; для подраздела — ключ сворачивания */
  id: string
  label: ReactNode
  /** справа: счётчик или статус («работает», «2», «вчера»). Прячется при ширине < 230. */
  meta?: ReactNode
  metaTone?: SideTone
  /** живая сессия — префикс «● » */
  live?: boolean
  /** выбранный пункт — фон --hover */
  active?: boolean
  /** служебный пункт («+ новая сессия», «показать все») — цвет --dim */
  action?: boolean
  /** подсказка при наведении */
  title?: string
  onSelect?: () => void
  /** кнопки справа в строке — видны при наведении (на телефоне всегда) */
  actions?: SideAction[]
  /** не закрывать шторку на телефоне после клика (правка на месте) */
  keepOpen?: boolean
  /** есть дети — это подраздел уровня 1 со стрелкой; дети — уровень 2 */
  children?: SideItem[]
  /** подраздел свёрнут по умолчанию */
  defaultFolded?: boolean
}

export interface SideAction {
  id: string
  /** значок или короткое слово: «×», «удалить» */
  label: ReactNode
  title: string
  /** опасное — красным */
  danger?: boolean
  /** подтверждение на месте: первый клик показывает этот текст, второй — выполняет */
  confirm?: string
  run: () => void
}

export interface SideSection {
  /** ключ сворачивания раздела */
  id: string
  /** СЕССИИ, СХЕМЫ, ДОСКИ… (регистр приводит CSS) */
  title: string
  /** счётчик справа от заголовка (прячется при ширине < 230) */
  count?: ReactNode
  /** «+» справа в заголовке */
  onAdd?: () => void
  addTitle?: string
  items: SideItem[]
  /** своё содержимое под пунктами (например, «Улучшения» Конструктора) */
  footer?: ReactNode
  defaultFolded?: boolean
}

export interface SideMenuProps {
  /** id раздела оболочки — префикс ключей sideFold */
  screen: string
  sections: SideSection[]
  /** ниже всех разделов, внутри прокрутки */
  children?: ReactNode
}

/** Свёрнут ли узел: явный выбор пользователя, иначе значение по умолчанию. */
export function useFold(key: string, byDefault = false): [boolean, () => void] {
  const value = useAppStore((s) => s.sideFold[key])
  const toggleFold = useAppStore((s) => s.toggleFold)
  const folded = value === undefined ? byDefault : value
  return [
    folded,
    () => {
      // первый клик по свёрнутому по умолчанию узлу должен его раскрыть
      if (value === undefined && byDefault) useAppStore.setState((s) => ({ sideFold: { ...s.sideFold, [key]: false } }))
      else toggleFold(key)
    },
  ]
}

/** Закрыть шторку на телефоне после выбора пункта. */
export function closeDrawerAfterSelect(): void {
  if (isMobileLayout()) useAppStore.getState().setMobSide(false)
}

export function SideMenu({ screen, sections, children }: SideMenuProps) {
  return (
    <div className="abx-side-menu">
      {sections.map((section) => (
        <SideSectionView key={section.id} screen={screen} section={section} />
      ))}
      {children}
    </div>
  )
}

function SideSectionView({ screen, section }: { screen: string; section: SideSection }) {
  const [folded, toggle] = useFold(`${screen}:${section.id}`, section.defaultFolded)
  return (
    <div className="abx-side-sec">
      <SideHeader
        title={section.title}
        count={section.count}
        open={!folded}
        onToggle={toggle}
        onAdd={section.onAdd}
        addTitle={section.addTitle}
      />
      {folded ? null : (
        <>
          <div className="abx-side-rows">
            {section.items.map((item) => (
              <SideItemView key={item.id} screen={screen} item={item} />
            ))}
          </div>
          {section.footer}
        </>
      )}
    </div>
  )
}

export interface SideHeaderProps {
  title: string
  count?: ReactNode
  open: boolean
  onToggle: () => void
  onAdd?: () => void
  addTitle?: string
}

/** Заголовок уровня 0. Экспортирован для своих блоков меню («Улучшения»). */
export function SideHeader({ title, count, open, onToggle, onAdd, addTitle }: SideHeaderProps) {
  return (
    <div
      className="abx-side-head"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onToggle()
        }
      }}
    >
      <Chevron open={open} className="abx-side-head__chev" />
      <span className="abx-side-head__title">{title}</span>
      {count !== undefined && count !== null && count !== '' ? (
        <span className="abx-side-head__count abx-side__meta">{count}</span>
      ) : null}
      <span className="abx-side-head__gap" />
      {onAdd ? (
        <button
          className="abx-side-head__add"
          title={addTitle ?? 'добавить'}
          onClick={(event) => {
            event.stopPropagation()
            onAdd()
          }}
        >
          +
        </button>
      ) : null}
    </div>
  )
}

function SideItemView({ screen, item }: { screen: string; item: SideItem }) {
  const isGroup = Array.isArray(item.children)
  const [folded, toggle] = useFold(`${screen}:${item.id}`, item.defaultFolded)
  if (!isGroup) return <SideRow item={item} level={1} />
  return (
    <>
      <SideRow
        item={item}
        level={1}
        chevron={folded ? 'closed' : 'open'}
        onClick={() => {
          toggle()
          item.onSelect?.()
        }}
      />
      {folded ? null : item.children!.map((child) => <SideRow key={child.id} item={child} level={2} />)}
    </>
  )
}

export interface SideRowProps {
  item: SideItem
  level: 1 | 2
  /** стрелка: нет (место держится), раскрыто, свёрнуто */
  chevron?: 'none' | 'open' | 'closed'
  /** своё действие вместо item.onSelect + закрытия шторки */
  onClick?: () => void
}

/** Строка уровня 1/2: высота 30, gap 10, radius 7. */
export function SideRow({ item, level, chevron = 'none', onClick }: SideRowProps) {
  // какое действие ждёт второго клика (подтверждение на месте, без confirm())
  const [armed, setArmed] = useState<string | null>(null)
  useEffect(() => {
    if (!armed) return
    const timer = window.setTimeout(() => setArmed(null), 4000)
    return () => window.clearTimeout(timer)
  }, [armed])
  const run = () => {
    if (onClick) {
      onClick()
      return
    }
    item.onSelect?.()
    if (!item.keepOpen) closeDrawerAfterSelect()
  }
  const cls = [
    'abx-side-row',
    level === 2 ? 'is-l2' : 'is-l1',
    item.active ? 'is-active' : '',
    item.action ? 'is-action' : '',
  ].filter(Boolean).join(' ')
  return (
    <div
      className={cls}
      role="button"
      tabIndex={0}
      title={item.title}
      aria-current={item.active ? 'true' : undefined}
      aria-expanded={chevron === 'none' ? undefined : chevron === 'open'}
      onClick={run}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          run()
        }
      }}
    >
      <Chevron open={chevron === 'open'} hidden={chevron === 'none'} className="abx-side-row__chev" />
      <span className="abx-side-row__label">
        {item.live ? '● ' : ''}
        {item.label}
      </span>
      {item.meta !== undefined && item.meta !== null && item.meta !== '' ? (
        <span className={`abx-side-row__meta abx-side__meta tone-${item.metaTone ?? 'dim'}`}>{item.meta}</span>
      ) : null}
      {item.actions?.length ? (
        <span className={`abx-side-row__acts${armed ? ' is-armed' : ''}`}>
          {item.actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`abx-side-row__act${a.danger ? ' is-danger' : ''}${armed === a.id ? ' is-armed' : ''}`}
              title={a.title}
              aria-label={a.title}
              onClick={(event) => {
                event.stopPropagation()
                if (a.confirm && armed !== a.id) {
                  setArmed(a.id)
                  return
                }
                setArmed(null)
                a.run()
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {armed === a.id ? a.confirm : a.label}
            </button>
          ))}
        </span>
      ) : null}
    </div>
  )
}
